import { generatePath } from '@/services/router/generate-path';

/*
 * `generatePath` rather than `router.buildLocation`, which these tree-owned
 * routes would otherwise prefer: this module is a plain one, with no router to
 * read, and none of these hrefs carries a search param — the case where
 * `buildLocation` would quote the value.
 */

/**
 * Where each thing that uses an item lives.
 *
 * Every one of these opens in a new tab: the point of the usage tab is to
 * go and look at what is in the way, and losing the item's own page while
 * doing it would mean finding it again afterwards.
 */

export function campaignHref(workspaceId: number, campaignId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
    workspaceId: String(workspaceId),
    campaignId: String(campaignId),
  });
}

export function segmentHref(workspaceId: number, segmentId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
    workspaceId: String(workspaceId),
    segmentId: String(segmentId),
  });
}

export function newsletterHref(workspaceId: number, newsletterId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/overview/recipients', {
    workspaceId: String(workspaceId),
    newsletterId: String(newsletterId),
  });
}

export function templateHref(workspaceId: number, templateId: number): string {
  return generatePath('/workspaces/:workspaceId/template/:templateId', {
    workspaceId: String(workspaceId),
    templateId: String(templateId),
  });
}

export function layoutHref(workspaceId: number, layoutId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/layouts/:layoutId', {
    workspaceId: String(workspaceId),
    layoutId: String(layoutId),
  });
}

export function snippetHref(workspaceId: number, snippetId: number): string {
  return generatePath('/workspaces/:workspaceId/snippets/:snippetId', {
    workspaceId: String(workspaceId),
    snippetId: String(snippetId),
  });
}

/**
 * A Design Studio asset's editor.
 *
 * The generic editor route takes any node id, so every asset links whatever
 * its type — a type this page has not seen is still reachable, which is what
 * the page it replaces does. WhatsApp is the one type with an editor of its
 * own; the page this replaces leaves it to the generic route and lets that
 * work out what it is holding.
 */
export function designStudioHref(workspaceId: number, type: string | null | undefined, id: string): string {
  // Encoded: path substitution writes a value as it is, and the id comes off
  // the wire.
  const assetId = encodeURIComponent(id);

  if (type === 'WHATSAPP') {
    return generatePath('/workspaces/:workspaceId/design-studio/editor/whatsapp/:id', {
      workspaceId: String(workspaceId),
      id: assetId,
    });
  }
  return generatePath('/workspaces/:workspaceId/design-studio/editor/:id', {
    workspaceId: String(workspaceId),
    id: assetId,
  });
}
